import { Link } from "react-router-dom";
import { Badge, Card } from "@vb/ui";
import { Seo } from "../lib/seo.js";
import { Section } from "../components/Section.js";

interface PostCard {
  slug: string;
  title: string;
  dek: string;
  date: string;
  readingTime: string;
  category: string;
}

const POSTS: PostCard[] = [
  {
    slug: "/blog/how-provably-fair-works",
    title: "How provably-fair crash games actually work — and how to verify one in your browser.",
    dek: "Threat model, commit-reveal, the inverse-uniform CDF, and a paste-in verifier you can run without trusting any operator (us included).",
    date: "2026-05-05",
    readingTime: "9 min read",
    category: "Engineering",
  },
  {
    slug: "/blog/rev-share-vs-licensing",
    title: "Revenue share or fixed licensing — what's actually better for a mid-tier crypto operator?",
    dek: "Where the Spribe/Smartsoft/Hacksaw deal sheets actually land, why integration fees dwarf list pricing, and the GGR threshold above which licensing wins.",
    date: "2026-05-05",
    readingTime: "7 min read",
    category: "B2B economics",
  },
  {
    slug: "/blog/spribe-alternative",
    title: "Spribe Aviator alternatives in 2026 — what to evaluate.",
    dek: "Honest comparison of five Aviator-class providers — Vault Breaker, Smartsoft JetX, Turbo Games, Hacksaw, BGaming — with a decision tree for direct vs aggregator-locked stacks.",
    date: "2026-05-05",
    readingTime: "6 min read",
    category: "iGaming stack",
  },
];

export function Blog() {
  return (
    <>
      <Seo
        title="Field notes · Vault Breaker Studios"
        description="Long-form posts on provably-fair math, B2B casino economics, and the crypto iGaming stack."
        path="/blog"
      />

      <Section style={{ paddingBottom: 32 }}>
        <Badge kind="fair" style={{ marginBottom: 18 }}>
          FIELD NOTES
        </Badge>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "clamp(44px, 6vw, 72px)",
            lineHeight: 1,
            margin: 0,
            color: "var(--fg-1)",
            textTransform: "uppercase",
          }}
        >
          Field notes
        </h1>
        <p
          style={{
            fontSize: 17,
            color: "var(--fg-2)",
            marginTop: 14,
            maxWidth: 720,
            lineHeight: 1.55,
          }}
        >
          Long-form posts on provably-fair math, B2B economics, and the iGaming stack. Written by
          the people who'd ship the integration if you bought it.
        </p>
      </Section>

      <Section>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: 16,
          }}
        >
          {POSTS.map((p) => (
            <Link
              key={p.slug}
              to={p.slug}
              style={{ textDecoration: "none", color: "inherit", display: "block" }}
            >
              <Card
                style={{
                  padding: 22,
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                  height: "100%",
                  transition: "border-color 120ms ease",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.borderColor = "var(--cyan)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.borderColor = "";
                }}
              >
                <span
                  className="label"
                  style={{
                    fontSize: 10,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: "var(--cyan)",
                  }}
                >
                  {p.category}
                </span>
                <h2
                  style={{
                    margin: 0,
                    fontFamily: "var(--font-display)",
                    fontSize: 22,
                    color: "var(--fg-1)",
                    lineHeight: 1.2,
                    letterSpacing: "0.01em",
                  }}
                >
                  {p.title}
                </h2>
                <p style={{ margin: 0, fontSize: 13, color: "var(--fg-2)", lineHeight: 1.6 }}>
                  {p.dek}
                </p>
                <div
                  style={{
                    marginTop: "auto",
                    paddingTop: 10,
                    display: "flex",
                    gap: 12,
                    fontFamily: "var(--font-mono)",
                    fontSize: 11,
                    color: "var(--fg-3)",
                  }}
                >
                  <span>{p.date}</span>
                  <span>·</span>
                  <span>{p.readingTime}</span>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      </Section>

      <Section>
        <Card style={{ padding: 22, display: "grid", gap: 10 }}>
          <p style={{ margin: 0, fontSize: 14, color: "var(--fg-2)", lineHeight: 1.6 }}>
            More posts coming. Subscribe via{" "}
            <a href="mailto:hello@vaultbreaker.dev" style={{ color: "var(--cyan)" }}>
              hello@vaultbreaker.dev
            </a>
            {" "}— one email per post, never more than monthly, no marketing copy.
          </p>
        </Card>
      </Section>
    </>
  );
}
